/**
 * CertPathPage: "Find your path", at /#/cert-path (Coleman, 2026-11-02).
 *
 * What a good sales call does, as a few taps: ask why they're here, who
 * they coach, what they want to fix (only if that's why they came) and how
 * much time they have. Then a result built around their answers, the price,
 * Enroll, "What's holding you back?", and a call always one tap away.
 *
 * Some coaches don't have a pain point. They want the name, or one big
 * course for everything. The first question sorts that out and the result
 * speaks to it (certPath.js).
 *
 * Every answer is logged (cert_path_*), so we learn why people buy.
 * Web only, like the rest of the cert sales surfaces.
 */
import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, Phone, ShieldCheck } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { MOTIVES, ROLES, PROBLEMS, TIME, MAX_PROBLEMS, pathSteps, buildPath } from "../certification/certPath";
import { CERT_TESTIMONIALS, CERT_PATHWAY } from "../certification/constants";
import { logEvent } from "../utils/analytics";
import { isNative } from "../../utils/platform";
import { metaTrack } from "../../utils/metaPixel";
import { captureAdSource } from "../../utils/adSource";
import { startCertCheckout, certPricing, fetchCountry, CERT_3PAY_COUNT } from "../utils/certCheckout";
import { CallModal, ObjectionPicker } from "./certSalesParts";

const BG = "#0A0A0A";
const CARD = "#121212";
const LINE = "rgba(255,255,255,0.08)";
const TEXT = "#F5F0E8";
const MID = "#B8B2A4";
const DIM = "#7D786E";
const HEAD = "'Bebas Neue', sans-serif";
const money = (n) => `$${n.toLocaleString("en-US")}`;

const QUESTIONS = {
  motive: { title: "What brings you to the certification?", options: MOTIVES },
  role: { title: "Who do you coach?", options: ROLES },
  problems: { title: `What do you most want to get better at? Pick up to ${MAX_PROBLEMS}.`, options: PROBLEMS, multi: true },
  time: { title: "How much time could you give it each week?", options: TIME },
};

export function CertPathPage() {
  const [answers, setAnswers] = useState({ problems: [] });
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [plan, setPlan] = useState("full");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [country, setCountry] = useState(null);
  const price = certPricing(country);

  useEffect(() => {
    captureAdSource();
    logEvent("cert_path_view", {});
    metaTrack("ViewContent", { content_name: "BAM Certification path" });
    let on = true;
    fetchCountry().then((c) => { if (on) setCountry(c); });
    if (new URLSearchParams(window.location.search).get("cert_cancelled") === "1") {
      setError("Checkout was cancelled. You weren't charged.");
    }
    return () => { on = false; };
  }, []);

  const steps = pathSteps(answers);
  const key = steps[step];
  const q = QUESTIONS[key];

  const choose = (value) => {
    const next = { ...answers, [key]: value };
    // Changing why they came can drop the problems step, so clear stale picks.
    if (key === "motive" && value !== "specific") next.problems = [];
    setAnswers(next);
    logEvent("cert_path_answer", { step: key, value });
    advance(next);
  };

  const advance = (next) => {
    const nextSteps = pathSteps(next);
    if (step + 1 < nextSteps.length) { setStep(step + 1); window.scrollTo(0, 0); return; }
    setDone(true);
    window.scrollTo(0, 0);
    logEvent("cert_path_complete", { motive: next.motive, role: next.role, problems: (next.problems || []).join(","), time: next.time });
  };

  const toggleProblem = (id) => {
    const cur = answers.problems || [];
    const has = cur.includes(id);
    if (!has && cur.length >= MAX_PROBLEMS) return;
    setAnswers({ ...answers, problems: has ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const openCall = (where) => { logEvent("cert_sales_call_click", { where, from: "cert_path" }); setCallOpen(true); };

  const enroll = async (where) => {
    setBusy(true);
    setError(null);
    metaTrack("InitiateCheckout", { value: plan === "3pay" ? price.pay : price.full, currency: "USD", content_name: "BAM Certification" });
    logEvent("cert_sales_checkout_start", { plan, where, from: "cert_path", motive: answers.motive, intl: price.intl });
    const r = await startCertCheckout({ plan, returnHash: "#/cert-path", country });
    if (r.alreadyPurchased) { window.location.hash = "#/certified"; return; }
    if (r.url) { window.location.href = r.url; return; }
    setError(r.error);
    setBusy(false);
  };

  if (isNative()) return null;

  return (
    <div style={{ background: BG, color: TEXT, minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 18px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="#/certification" style={{ display: "flex", alignItems: "center", gap: 10, color: TEXT, textDecoration: "none" }}>
            <img src={BAM_LOGO_PNG} alt="By Any Means" style={{ height: 28 }} />
            <span style={{ fontFamily: HEAD, letterSpacing: 1.5, fontSize: 17 }}>BAM CERTIFICATION</span>
          </a>
          <button onClick={() => openCall("top_bar")} style={{ ...ghostBtn, padding: "8px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}><Phone size={14} /> Book a call</button>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 18px 80px" }}>
        {!done ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
              {step > 0 ? (
                <button onClick={() => setStep(step - 1)} aria-label="Back" style={{ ...ghostBtn, padding: 8, display: "flex" }}><ArrowLeft size={16} /></button>
              ) : <span style={{ width: 34 }} />}
              <div style={{ flex: 1, height: 4, background: LINE, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${((step + 1) / steps.length) * 100}%`, height: "100%", background: GOLD, transition: "width .25s" }} />
              </div>
              <span style={{ fontSize: 12, color: DIM }}>{step + 1} of {steps.length}</span>
            </div>
            {step === 0 && <div style={{ fontSize: 12, letterSpacing: 2.5, textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 8 }}>Find your path · 60 seconds</div>}
            <h1 style={{ fontFamily: HEAD, fontSize: 38, lineHeight: 1.05, letterSpacing: 0.5, margin: "0 0 20px" }}>{q.title}</h1>
            <div style={{ display: "grid", gap: 10 }}>
              {q.options.map((o) => {
                const on = q.multi ? (answers.problems || []).includes(o.id) : answers[key] === o.id;
                return (
                  <button key={o.id} onClick={() => (q.multi ? toggleProblem(o.id) : choose(o.id))} style={{
                    textAlign: "left", padding: "16px 18px", borderRadius: 12, cursor: "pointer", color: TEXT,
                    background: on ? `${GOLD}18` : CARD, border: `1.5px solid ${on ? GOLD : LINE}`, display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <span style={{ flex: 1 }}>
                      <span style={{ display: "block", fontWeight: 600, fontSize: 16 }}>{o.label}</span>
                      {o.hint && <span style={{ display: "block", color: MID, fontSize: 13, marginTop: 4 }}>{o.hint}</span>}
                    </span>
                    {on && <Check size={18} color={GOLD} />}
                  </button>
                );
              })}
            </div>
            {q.multi && (
              <button disabled={!(answers.problems || []).length} onClick={() => { logEvent("cert_path_answer", { step: "problems", value: answers.problems.join(",") }); advance(answers); }}
                style={{ ...goldBtn, marginTop: 18, width: "100%", justifyContent: "center", opacity: (answers.problems || []).length ? 1 : 0.5 }}>
                Continue <ArrowRight size={16} />
              </button>
            )}
            <div style={{ marginTop: 26, textAlign: "center", fontSize: 13, color: DIM }}>
              Rather just talk it through? <button onClick={() => openCall("quiz")} style={linkBtn}>Book a 20-minute call</button>
              {" "}or <a href="#/certification" style={{ color: MID }}>see the full course page</a>.
            </div>
          </>
        ) : (
          <Result answers={answers} price={price} plan={plan} setPlan={setPlan} busy={busy} error={error} enroll={enroll} openCall={openCall}
            restart={() => { setDone(false); setStep(0); setAnswers({ problems: [] }); logEvent("cert_path_restart", {}); }} />
        )}
      </div>
      {callOpen && <CallModal onClose={() => setCallOpen(false)} />}
    </div>
  );
}

function Result({ answers, price, plan, setPlan, busy, error, enroll, openCall, restart }) {
  const r = buildPath(answers);
  const t = CERT_TESTIMONIALS[0];
  const label = plan === "3pay" ? `Start with ${money(price.pay)} today` : `Enroll now, ${money(price.full)}`;
  return (
    <div>
      <div style={{ fontSize: 12, letterSpacing: 2.5, textTransform: "uppercase", color: GOLD, fontWeight: 700 }}>Your path</div>
      <h1 style={{ fontFamily: HEAD, fontSize: 44, lineHeight: 1, letterSpacing: 0.5, margin: "8px 0 14px" }}>{r.headline}</h1>
      <p style={para}>{r.intro}</p>
      {r.persona && <p style={{ ...para, color: TEXT }}><strong style={{ color: GOLD }}>{r.persona.title}.</strong> {r.persona.desc}</p>}

      <div style={{ display: "grid", gap: 10, margin: "20px 0" }}>
        {r.focus.map((u) => (
          <div key={u.id} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18 }}>
            <div style={{ fontFamily: HEAD, color: GOLD, fontSize: 16 }}>Unit {String(u.id).padStart(2, "0")}</div>
            <div style={{ fontWeight: 700, margin: "2px 0 10px" }}>{u.title}</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
              {u.outcomes.map((o) => <li key={o} style={{ display: "flex", gap: 8, color: MID, fontSize: 14 }}><Check size={15} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />{o}</li>)}
            </ul>
          </div>
        ))}
        <div style={{ color: DIM, fontSize: 13 }}>Plus every other unit: all nine are included.</div>
      </div>

      {r.showCredentialExtras && (
        <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>How you earn it</div>
          {CERT_PATHWAY.map((s) => <div key={s.id} style={{ color: MID, fontSize: 14, marginTop: 6 }}><strong style={{ color: TEXT }}>{s.id}. {s.title}.</strong> {s.desc}</div>)}
        </div>
      )}

      <p style={{ ...para, color: TEXT }}>{r.timeline}</p>

      {t && (
        <figure style={{ margin: "22px 0", padding: 20, borderLeft: `3px solid ${GOLD}`, background: CARD, borderRadius: 8 }}>
          <blockquote style={{ margin: 0, color: TEXT, fontSize: 15, lineHeight: 1.65 }}>"{t.quote.length > 260 ? t.quote.slice(0, t.quote.lastIndexOf(" ", 260)) + "..." : t.quote}"</blockquote>
          <figcaption style={{ marginTop: 10, color: GOLD, fontWeight: 700, fontSize: 14 }}>{t.name} <span style={{ color: DIM, fontWeight: 400 }}>· {t.role}</span></figcaption>
        </figure>
      )}

      <div style={{ background: CARD, border: `1px solid ${GOLD}55`, borderRadius: 16, padding: 22, textAlign: "center" }}>
        {price.intl && (
          <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: `${GOLD}1A`, border: `1px solid ${GOLD}55` }}>
            <div style={{ color: GOLD, fontWeight: 800 }}>International coach pricing: {money(price.saving)} off</div>
            <div style={{ color: MID, fontSize: 12, marginTop: 4 }}>Applied automatically for coaches outside the US.</div>
          </div>
        )}
        <div role="radiogroup" aria-label="Payment option" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {[["full", money(price.full), price.wasFull && money(price.wasFull), "Pay in full"],
            ["3pay", `${CERT_3PAY_COUNT} × ${money(price.pay)}`, price.wasPay && `${CERT_3PAY_COUNT} × ${money(price.wasPay)}`, "Monthly, stops after 3"]].map(([id, title, was, sub]) => (
            <button key={id} role="radio" aria-checked={plan === id} onClick={() => setPlan(id)} style={{ padding: "12px 8px", borderRadius: 10, cursor: "pointer", color: TEXT, background: plan === id ? `${GOLD}18` : "transparent", border: `1.5px solid ${plan === id ? GOLD : LINE}` }}>
              {was && <div style={{ fontSize: 12, color: DIM, textDecoration: "line-through" }}>{was}</div>}
              <div style={{ fontFamily: HEAD, fontSize: 26, color: plan === id ? GOLD : TEXT }}>{title}</div>
              <div style={{ fontSize: 12, color: MID }}>{sub}</div>
            </button>
          ))}
        </div>
        <button onClick={() => enroll("path_result")} disabled={busy} style={{ ...goldBtn, width: "100%", justifyContent: "center", opacity: busy ? 0.7 : 1 }}>
          {busy ? <><Loader2 size={16} /> Opening secure checkout</> : <>{label} <ArrowRight size={16} /></>}
        </button>
        {error && <div role="alert" style={{ marginTop: 10, color: "#F2A3A3", fontSize: 13 }}>{error}</div>}
        <button onClick={() => openCall("path_result")} style={{ ...ghostBtn, width: "100%", marginTop: 10, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 8 }}><Phone size={15} /> Book a call instead</button>
        <div style={{ marginTop: 12, fontSize: 12, color: DIM, display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
          <ShieldCheck size={13} /> 7-day refund window · No account needed to pay
        </div>
      </div>

      <h2 style={{ fontFamily: HEAD, fontSize: 32, letterSpacing: 0.5, margin: "34px 0 14px" }}>What's holding you back?</h2>
      <ObjectionPicker from="cert_path" enrollLabel={label} onEnroll={enroll} onCall={openCall} />

      <div style={{ marginTop: 30, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
        <a href="#/certification" style={{ color: MID }}>See the full course page</a>
        <button onClick={restart} style={linkBtn}>Start over</button>
      </div>
    </div>
  );
}

const para = { fontSize: 16, lineHeight: 1.65, color: MID, margin: "0 0 12px" };
const goldBtn = { display: "inline-flex", alignItems: "center", gap: 8, background: GOLD, color: "#111", border: "none", borderRadius: 10, padding: "14px 20px", fontWeight: 800, fontSize: 15, cursor: "pointer" };
const ghostBtn = { background: "transparent", color: TEXT, border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer" };
const linkBtn = { background: "none", border: "none", color: GOLD, cursor: "pointer", fontSize: 13, padding: 0, textDecoration: "underline" };

export default CertPathPage;
